import React from "react";
import { useTranslation } from "react-i18next";
import { Settings, Zap } from "lucide-react";
import { Button } from "@core/index";
import { gradientClasses } from "../constants";

export type CreationMode = "basic" | "advanced";

export interface BasicAdvancedToggleProps {
  mode: CreationMode;
  onModeChange: (mode: CreationMode) => void;
  themeColor: string;
  className?: string;
  disabled?: boolean;
}

const BasicAdvancedToggle: React.FC<BasicAdvancedToggleProps> = ({
  mode,
  onModeChange,
  themeColor,
  className = "",
  disabled = false,
}) => {
  const { t } = useTranslation();

  const getThemeClasses = (isActive: boolean) => {
    if (disabled) {
      return "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed";
    }

    if (isActive) {
      const gradientClass =
        gradientClasses[themeColor as keyof typeof gradientClasses] ||
        gradientClasses.blue;
      return `bg-gradient-to-r ${gradientClass} text-white shadow-lg`;
    }

    return "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-700";
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className="flex gap-2">
        <Button
          type="button"
          onClick={() => !disabled && onModeChange("basic")}
          disabled={disabled}
          variant="solid"
          size="md"
          rounded="lg"
          className={`flex items-center gap-2 px-4 py-2 transition-all duration-200 ${getThemeClasses(mode === "basic")}`}
        >
          <Zap size={16} />
          <span className="font-medium">{t("common.basic", "Basic")}</span>
        </Button>

        <Button
          type="button"
          onClick={() => !disabled && onModeChange("advanced")}
          disabled={disabled}
          variant="solid"
          size="md"
          rounded="lg"
          className={`flex items-center gap-2 px-4 py-2 transition-all duration-200 ${getThemeClasses(mode === "advanced")}`}
        >
          <Settings size={16} />
          <span className="font-medium">
            {t("common.advanced", "Advanced")}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default BasicAdvancedToggle;
